import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MessageSquare, Send, Loader2, RefreshCw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AdminChatInbox = () => {
  const [chats, setChats] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);

  const load = () =>
    axios.get(`${API}/admin/chats`, { withCredentials: true })
      .then((r) => setChats(r.data.chats))
      .catch(() => setChats([]));

  useEffect(() => {
    load();
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, []);

  const sendReply = async (sessionId) => {
    const text = reply.trim();
    if (!text || sending) return;
    setSending(true);
    try {
      await axios.post(`${API}/admin/chats/${sessionId}/reply`, { message: text }, { withCredentials: true });
      setReply("");
      load();
    } catch {
      toast.error("Reply failed to send.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="rounded-3xl border border-white/15 bg-[#0F1B31]/80 p-8" data-testid="admin-chat-inbox">
      <div className="flex items-center justify-between mb-6">
        <p className="font-mono-label text-xs uppercase tracking-[0.3em] text-slate-400 flex items-center gap-2">
          <MessageSquare className="h-4 w-4" /> AI Chat Inbox
        </p>
        <button onClick={load} data-testid="chat-inbox-refresh" className="text-slate-500 hover:text-white transition-colors duration-300" aria-label="Refresh chats">
          <RefreshCw className="h-4 w-4" />
        </button>
      </div>
      {chats === null ? (
        <Loader2 className="h-5 w-5 animate-spin text-slate-500" />
      ) : chats.length === 0 ? (
        <p className="text-sm text-slate-500" data-testid="chat-inbox-empty">No conversations yet.</p>
      ) : (
        <div className="space-y-3">
          {chats.map((c) => (
            <div key={c.session_id} className="rounded-2xl border border-white/10 overflow-hidden" data-testid={`chat-thread-${c.session_id.slice(0, 8)}`}>
              <button
                onClick={() => setOpenId(openId === c.session_id ? null : c.session_id)}
                className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left hover:bg-white/[0.03] transition-colors duration-300"
                data-testid={`chat-thread-toggle-${c.session_id.slice(0, 8)}`}
              >
                <div className="min-w-0">
                  <p className="font-mono-label text-[10px] uppercase tracking-[0.2em] text-slate-500">
                    Visitor {c.session_id.slice(0, 8)} · {c.messages.length} messages
                  </p>
                  <p className="text-sm text-slate-300 truncate mt-1">
                    {c.messages[c.messages.length - 1]?.content}
                  </p>
                </div>
                <span className="text-xs text-slate-600 whitespace-nowrap">
                  {new Date(c.last_at).toLocaleString()}
                </span>
              </button>
              {openId === c.session_id && (
                <div className="border-t border-white/10 px-5 py-4">
                  <div className="max-h-72 overflow-y-auto space-y-3 pr-1">
                    {c.messages.map((m, i) => (
                      <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                        <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                          m.role === "user"
                            ? "bg-white text-[#060D1A] rounded-br-md"
                            : m.role === "owner"
                              ? "bg-white/[0.14] text-white rounded-bl-md border border-white/20"
                              : "bg-white/[0.06] text-slate-300 rounded-bl-md"
                        }`}>
                          {m.role === "owner" && (
                            <p className="mb-1 font-mono-label text-[9px] uppercase tracking-[0.2em] text-slate-500">You</p>
                          )}
                          {m.content}
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 flex items-center gap-2">
                    <input
                      value={reply}
                      onChange={(e) => setReply(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && sendReply(c.session_id)}
                      placeholder="Reply personally as Owen…"
                      data-testid={`chat-reply-input-${c.session_id.slice(0, 8)}`}
                      className="flex-1 rounded-full border border-white/15 bg-white/[0.05] px-4 py-2.5 text-sm text-white placeholder:text-slate-600 outline-none focus:border-white/40"
                    />
                    <button
                      onClick={() => sendReply(c.session_id)}
                      disabled={sending || !reply.trim()}
                      data-testid={`chat-reply-send-${c.session_id.slice(0, 8)}`}
                      className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white text-[#060D1A] transition-colors duration-300 hover:bg-slate-300 disabled:opacity-50"
                      aria-label="Send reply"
                    >
                      {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
